import { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { customIPPacksAPI, researchAPI } from '../lib/api';
import { toast } from 'sonner';
import {
  ArrowLeft,
  Palette,
  Plus,
  Trash2,
  Edit,
  Check,
  X,
  ImageIcon,
  Video,
  MessageSquare,
  FileText,
  Loader2,
  Send,
  CheckCircle,
  Clock,
  Package,
  Play,
  ExternalLink,
  Instagram,
  Sparkles,
  User,
  Copy,
  Mic,
  Pause,
} from 'lucide-react';
import { Button } from '../components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../components/ui/dialog';
import { Textarea } from '../components/ui/textarea';

const SECTION_CONFIG = {
  customer_reference: {
    title: 'Customer Reference',
    description: 'Upload customer reference images and screenshots of conversations',
    icon: MessageSquare,
  },
  cad_quote: {
    title: 'CAD / Quote',
    description: 'Upload renders, CAD designs, and quotation screenshots',
    icon: FileText,
  },
  final_product: {
    title: 'Final Product',
    description: 'Upload images and videos of the final product',
    icon: ImageIcon,
  },
  reviews: {
    title: 'Reviews',
    description: 'Add customer reviews - images and text',
    icon: MessageSquare,
  },
};

const STATUS_CONFIG = {
  draft: { label: 'Draft', color: 'bg-zinc-100 text-zinc-700' },
  pending_approval: { label: 'Pending Approval', color: 'bg-amber-100 text-amber-700' },
  approved: { label: 'Approved', color: 'bg-green-100 text-green-700' },
};

const CustomIPDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { isFounder, token } = useAuth();
  const fileInputRefs = useRef({});

  const [pack, setPack] = useState(null);
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState({});
  const [editing, setEditing] = useState(false);
  const [editForm, setEditForm] = useState({ title: '', price_sold: '' });
  const [script, setScript] = useState('');
  const [savingScript, setSavingScript] = useState(false);
  const [deleteMediaConfirm, setDeleteMediaConfirm] = useState(null);
  const [newReviewText, setNewReviewText] = useState('');
  const [addingReview, setAddingReview] = useState(false);
  const [previewMedia, setPreviewMedia] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [approving, setApproving] = useState(false);
  const [reelUrl, setReelUrl] = useState('');
  const [addingReel, setAddingReel] = useState(false);

  // Script generation state
  const [showGenDialog, setShowGenDialog] = useState(false);
  const [influencers, setInfluencers] = useState([]);
  const [loadingInfluencers, setLoadingInfluencers] = useState(false);
  const [selectedInfluencer, setSelectedInfluencer] = useState(null);
  const [generatingScript, setGeneratingScript] = useState(false);
  const [generatedScript, setGeneratedScript] = useState(null);

  // Voice note state
  const [uploadingVoiceNote, setUploadingVoiceNote] = useState(false);
  const [playingNoteId, setPlayingNoteId] = useState(null);
  const voiceNoteInputRef = useRef(null);
  const audioRefs = useRef({});

  useEffect(() => {
    fetchPack();
  }, [id]);

  const fetchPack = async () => {
    try {
      setLoading(true);
      const response = await customIPPacksAPI.getById(id);
      setPack(response.data);
      setEditForm({
        title: response.data.title || '',
        price_sold: response.data.price_sold || '',
      });
      setScript(response.data.script || '');
    } catch (error) {
      if (error.response?.status === 401) return;
      const detail = error.response?.data?.detail || error.message || 'Unknown error';
      console.error('Custom IP detail load failed:', error);
      toast.error(`Failed to load Custom IP pack: ${detail}`);
      navigate('/ideas/custom-ip');
    } finally {
      setLoading(false);
    }
  };

  const handleFileSelect = async (section, e) => {
    const files = Array.from(e.target.files);
    if (files.length === 0) return;

    setUploading((prev) => ({ ...prev, [section]: true }));
    let successCount = 0;

    for (const file of files) {
      try {
        await customIPPacksAPI.uploadMedia(id, section, file);
        successCount++;
      } catch (error) {
        console.error('Upload error:', error);
      }
    }

    if (successCount > 0) {
      toast.success(`${successCount} file${successCount > 1 ? 's' : ''} uploaded`);
    }

    setUploading((prev) => ({ ...prev, [section]: false }));
    fetchPack();
    e.target.value = '';
  };

  const handleDeleteMedia = async () => {
    if (!deleteMediaConfirm) return;
    const { section, mediaId } = deleteMediaConfirm;

    try {
      await customIPPacksAPI.deleteMedia(id, section, mediaId);
      toast.success('File deleted');
      setDeleteMediaConfirm(null);
      fetchPack();
    } catch (error) {
      toast.error('Failed to delete file');
    }
  };

  const handleSaveEdit = async () => {
    try {
      await customIPPacksAPI.update(id, editForm);
      toast.success('Updated');
      setEditing(false);
      fetchPack();
    } catch (error) {
      toast.error('Failed to update');
    }
  };

  const handleSaveScript = async () => {
    setSavingScript(true);
    try {
      await customIPPacksAPI.update(id, { script });
      toast.success('Script saved');
    } catch (error) {
      toast.error('Failed to save script');
    } finally {
      setSavingScript(false);
    }
  };

  const openGenerateDialog = async () => {
    setShowGenDialog(true);
    setSelectedInfluencer(null);
    setGeneratedScript(null);
    setLoadingInfluencers(true);
    try {
      const res = await researchAPI.listInfluencers();
      const analyzed = (res.data || []).filter(i => i.status === 'analyzed');
      setInfluencers(analyzed);
    } catch {
      toast.error('Failed to load influencers');
    } finally {
      setLoadingInfluencers(false);
    }
  };

  const handleGenerateScript = async () => {
    if (!selectedInfluencer) { toast.error('Select an influencer'); return; }
    try {
      setGeneratingScript(true);
      const res = await customIPPacksAPI.generateScript(id, selectedInfluencer);
      setGeneratedScript(res.data);
      toast.success('Script generated!');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Generation failed');
    } finally {
      setGeneratingScript(false);
    }
  };

  const applyGeneratedScript = () => {
    if (generatedScript?.generated_script) {
      setScript(generatedScript.generated_script);
      setShowGenDialog(false);
      toast.success('Script applied — remember to Save');
    }
  };

  const copyToClipboard = (text) => {
    navigator.clipboard.writeText(text);
    toast.success('Copied');
  };

  // Voice note handlers
  const handleVoiceNoteUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploadingVoiceNote(true);
    try {
      await customIPPacksAPI.uploadVoiceNote(id, file);
      toast.success('Voice note uploaded');
      fetchPack();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to upload voice note');
    } finally {
      setUploadingVoiceNote(false);
      if (voiceNoteInputRef.current) voiceNoteInputRef.current.value = '';
    }
  };

  const handleDeleteVoiceNote = async (noteId) => {
    try {
      await customIPPacksAPI.deleteVoiceNote(id, noteId);
      toast.success('Voice note deleted');
      fetchPack();
    } catch {
      toast.error('Failed to delete voice note');
    }
  };

  const togglePlayNote = (noteId) => {
    const audio = audioRefs.current[noteId];
    if (!audio) return;
    if (playingNoteId === noteId) {
      audio.pause();
      setPlayingNoteId(null);
    } else {
      // Pause any currently playing
      Object.values(audioRefs.current).forEach(a => a?.pause());
      audio.play();
      setPlayingNoteId(noteId);
    }
  };


  const handleAddTextReview = async () => {
    if (!newReviewText.trim()) return;

    setAddingReview(true);
    try {
      await customIPPacksAPI.addTextReview(id, newReviewText);
      toast.success('Review added');
      setNewReviewText('');
      fetchPack();
    } catch (error) {
      toast.error('Failed to add review');
    } finally {
      setAddingReview(false);
    }
  };

  const handleDeleteTextReview = async (reviewId) => {
    try {
      await customIPPacksAPI.deleteTextReview(id, reviewId);
      toast.success('Review deleted');
      fetchPack();
    } catch (error) {
      toast.error('Failed to delete review');
    }
  };

  const handleSubmitForApproval = async () => {
    if (!script.trim()) {
      toast.error('Please write a script before submitting for approval');
      return;
    }

    setSubmitting(true);
    try {
      // Save script first
      await customIPPacksAPI.update(id, { script });
      // Then submit for approval
      await customIPPacksAPI.submitForApproval(id);
      toast.success('Submitted for approval');
      fetchPack();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to submit');
    } finally {
      setSubmitting(false);
    }
  };

  const handleAddReferenceReel = async () => {
    if (!reelUrl.trim()) { toast.error('Please enter an Instagram URL'); return; }
    setAddingReel(true);
    try {
      await customIPPacksAPI.addReferenceReel(id, reelUrl.trim());
      toast.success('Reference reel added');
      setReelUrl('');
      fetchPack();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to add reference reel');
    } finally {
      setAddingReel(false);
    }
  };

  const handleDeleteReferenceReel = async (refId) => {
    try {
      await customIPPacksAPI.deleteReferenceReel(id, refId);
      toast.success('Reference reel removed');
      fetchPack();
    } catch { toast.error('Failed to delete reference reel'); }
  };

  const handleApprove = async () => {
    setApproving(true);
    try {
      const response = await customIPPacksAPI.approve(id);
      toast.success('Approved! Production pack created.');
      // Navigate to the production pack
      navigate(`/production-packs/${response.data.production_pack_id}`);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to approve');
    } finally {
      setApproving(false);
    }
  };

  const formatPrice = (price) => {
    if (!price) return '';
    return price.startsWith('$') ? price : `$${price}`;
  };

  const formatFileSize = (bytes) => {
    if (bytes < 1024) return `${bytes} B`;
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
    return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
  };

  if (loading) {
    return (
      <div className="animate-pulse space-y-6">
        <div className="h-8 w-64 bg-zinc-100 rounded" />
        <div className="h-96 bg-zinc-100 rounded" />
      </div>
    );
  }

  if (!pack) {
    return (
      <div className="text-center py-16">
        <p className="text-zinc-500">Pack not found</p>
      </div>
    );
  }

  const statusConfig = STATUS_CONFIG[pack.status] || STATUS_CONFIG.draft;
  const canEdit = pack.status === 'draft' || pack.status === 'pending_approval';
  const canSubmit = pack.status === 'draft';
  const canApprove = pack.status === 'pending_approval' && isFounder();

  return (
    <div className="space-y-6 fade-in" data-testid="custom-ip-detail">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-4">
          <Button
            variant="ghost"
            size="icon"
            onClick={() => navigate('/ideas/custom-ip')}
            className="hover:bg-zinc-100"
          >
            <ArrowLeft className="w-5 h-5" />
          </Button>
          <div>
            {editing ? (
              <input
                type="text"
                value={editForm.title}
                onChange={(e) => setEditForm({ ...editForm, title: e.target.value })}
                className="text-2xl font-medium border-b-2 border-zinc-300 focus:border-zinc-900 outline-none bg-transparent"
                style={{ fontFamily: 'Playfair Display, serif' }}
                autoFocus
              />
            ) : (
              <h1 className="text-2xl text-zinc-900" style={{ fontFamily: 'Playfair Display, serif' }}>
                {pack.title}
              </h1>
            )}
            <div className="flex items-center gap-3 mt-2">
              {editing ? (
                <input
                  type="text"
                  value={editForm.price_sold}
                  onChange={(e) => setEditForm({ ...editForm, price_sold: e.target.value })}
                  placeholder="Price Sold"
                  className="text-lg font-semibold text-zinc-700 border-b border-zinc-200 focus:border-zinc-900 outline-none bg-transparent"
                />
              ) : (
                pack.price_sold && (
                  <span className="text-lg font-semibold text-zinc-700">
                    {formatPrice(pack.price_sold)}
                  </span>
                )
              )}
              <span className={`inline-flex items-center px-2.5 py-1 rounded-full text-xs font-medium ${statusConfig.color}`}>
                {statusConfig.label}
              </span>
            </div>
          </div>
        </div>
        <div className="flex items-center gap-2">
          {editing ? (
            <>
              <Button variant="outline" onClick={() => setEditing(false)}>
                Cancel
              </Button>
              <Button onClick={handleSaveEdit} className="bg-zinc-900 hover:bg-zinc-800 text-white">
                <Check className="w-4 h-4 mr-2" />
                Save
              </Button>
            </>
          ) : (
            <>
              {canEdit && (
                <Button variant="outline" onClick={() => setEditing(true)}>
                  <Edit className="w-4 h-4 mr-2" />
                  Edit
                </Button>
              )}
              {pack.production_pack_id && (
                <Button
                  variant="outline"
                  onClick={() => navigate(`/production-packs/${pack.production_pack_id}`)}
                >
                  <Package className="w-4 h-4 mr-2" />
                  View Production
                </Button>
              )}
            </>
          )}
        </div>
      </div>

      {/* Voice Notes Section — at the top */}
      <div className="bg-white border border-zinc-200 rounded-sm overflow-hidden" data-testid="voice-notes-section">
        <div className="px-6 py-4 bg-zinc-50 border-b border-zinc-200 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <Mic className="w-5 h-5 text-zinc-600" />
            <div>
              <h3 className="font-medium text-zinc-900">Voice Notes</h3>
              <p className="text-xs text-zinc-500">Record or upload voice notes telling the ring's story — used for AI script generation</p>
            </div>
          </div>
          {canEdit && (
            <div>
              <input
                ref={voiceNoteInputRef}
                type="file"
                accept="audio/*"
                onChange={handleVoiceNoteUpload}
                className="hidden"
              />
              <Button
                size="sm"
                variant="outline"
                onClick={() => voiceNoteInputRef.current?.click()}
                disabled={uploadingVoiceNote}
                data-testid="upload-voice-note-btn"
              >
                {uploadingVoiceNote ? (
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                ) : (
                  <Plus className="w-4 h-4 mr-2" />
                )}
                Add Voice Note
              </Button>
            </div>
          )}
        </div>
        <div className="p-6">
          {(!pack.voice_notes || pack.voice_notes.length === 0) ? (
            <div className="text-center py-6 text-zinc-400 text-sm">
              <Mic className="w-6 h-6 mx-auto mb-2 opacity-40" />
              No voice notes yet — upload a recording of the ring's story
            </div>
          ) : (
            <div className="space-y-3">
              {pack.voice_notes.map((note) => {
                const streamUrl = customIPPacksAPI.getVoiceNoteStreamUrl(id, note.id);
                const isPlaying = playingNoteId === note.id;
                return (
                  <div key={note.id} className="flex items-center gap-3 p-3 bg-zinc-50 rounded-lg border border-zinc-100" data-testid={`voice-note-${note.id}`}>
                    <button
                      onClick={() => togglePlayNote(note.id)}
                      className={`w-9 h-9 rounded-full flex items-center justify-center shrink-0 transition-colors ${
                        isPlaying ? 'bg-amber-500 text-white' : 'bg-zinc-200 text-zinc-600 hover:bg-zinc-300'
                      }`}
                      data-testid={`play-voice-note-${note.id}`}
                    >
                      {isPlaying ? <Pause size={16} /> : <Play size={16} />}
                    </button>
                    <audio
                      ref={(el) => { audioRefs.current[note.id] = el; }}
                      src={streamUrl}
                      onEnded={() => setPlayingNoteId(null)}
                      preload="none"
                    />
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-medium text-zinc-800 truncate">{note.original_name || 'Voice Note'}</p>
                      <p className="text-xs text-zinc-400">
                        {note.recorder_name && `by ${note.recorder_name} · `}
                        {note.file_size ? `${(note.file_size / 1024).toFixed(0)} KB` : ''}
                        {note.created_at && ` · ${new Date(note.created_at).toLocaleDateString()}`}
                      </p>
                    </div>
                    {canEdit && (
                      <button
                        onClick={() => handleDeleteVoiceNote(note.id)}
                        className="text-zinc-400 hover:text-red-500 transition-colors p-1"
                        data-testid={`delete-voice-note-${note.id}`}
                      >
                        <Trash2 size={14} />
                      </button>
                    )}
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </div>

      {/* Media Sections */}
      <div className="space-y-8">
        {Object.entries(SECTION_CONFIG).map(([sectionKey, config]) => {
          const SectionIcon = config.icon;
          const sectionData = pack[sectionKey] || { media: [] };
          const media = sectionData.media || [];
          const isReviewsSection = sectionKey === 'reviews';
          const textReviews = isReviewsSection ? (sectionData.text_reviews || []) : [];

          return (
            <div key={sectionKey} className="bg-white border border-zinc-200 rounded-sm overflow-hidden">
              {/* Section Header */}
              <div className="px-6 py-4 bg-zinc-50 border-b border-zinc-200 flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <SectionIcon className="w-5 h-5 text-zinc-600" />
                  <div>
                    <h3 className="font-medium text-zinc-900">{config.title}</h3>
                    <p className="text-xs text-zinc-500">{config.description}</p>
                  </div>
                </div>
                {canEdit && (
                  <div>
                    <input
                      ref={(el) => (fileInputRefs.current[sectionKey] = el)}
                      type="file"
                      multiple
                      accept="image/*,video/*"
                      onChange={(e) => handleFileSelect(sectionKey, e)}
                      className="hidden"
                    />
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => fileInputRefs.current[sectionKey]?.click()}
                      disabled={uploading[sectionKey]}
                    >
                      {uploading[sectionKey] ? (
                        <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                      ) : (
                        <Plus className="w-4 h-4 mr-2" />
                      )}
                      Add Media
                    </Button>
                  </div>
                )}
              </div>

              {/* Section Content */}
              <div className="p-6">
                {media.length === 0 && (!isReviewsSection || textReviews.length === 0) && !(isReviewsSection && canEdit) ? (
                  <div className="text-center py-8 text-zinc-400 text-sm">
                    No files uploaded yet
                  </div>
                ) : (
                  <div className="space-y-4">
                    {/* Media Grid */}
                    {media.length > 0 && (
                      <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-4">
                        {media.map((item) => {
                          const mediaUrl = customIPPacksAPI.getMediaUrl(id, sectionKey, item.id);
                          return (
                          <div
                            key={item.id}
                            className="relative group aspect-square bg-zinc-100 rounded-sm overflow-hidden"
                          >
                            {item.is_video ? (
                              <>
                                <video
                                  src={mediaUrl}
                                  className="w-full h-full object-cover"
                                  muted
                                  playsInline
                                />
                                <div className="absolute inset-0 flex items-center justify-center">
                                  <Button
                                    size="icon"
                                    className="h-10 w-10 rounded-full bg-white/90 hover:bg-white text-zinc-900"
                                    onClick={() => setPreviewMedia({...item, file_path: mediaUrl})}
                                  >
                                    <Play className="w-5 h-5 ml-0.5" />
                                  </Button>
                                </div>
                              </>
                            ) : (
                              <img
                                src={mediaUrl}
                                alt={item.original_name}
                                className="w-full h-full object-cover cursor-pointer"
                                onClick={() => setPreviewMedia({...item, file_path: mediaUrl})}
                              />
                            )}
                            {canEdit && (
                              <Button
                                size="icon"
                                variant="ghost"
                                className="absolute top-1 right-1 h-7 w-7 bg-white/90 hover:bg-white text-red-600 opacity-0 group-hover:opacity-100 transition-opacity"
                                onClick={() => setDeleteMediaConfirm({ section: sectionKey, mediaId: item.id, name: item.original_name })}
                              >
                                <Trash2 className="w-4 h-4" />
                              </Button>
                            )}
                          </div>
                          );
                        })}
                      </div>
                    )}

                    {/* Text Reviews */}
                    {isReviewsSection && textReviews.length > 0 && (
                      <div className="space-y-3 mt-4">
                        <h4 className="text-sm font-medium text-zinc-700">Text Reviews</h4>
                        {textReviews.map((review) => (
                          <div
                            key={review.id}
                            className="bg-zinc-50 p-4 rounded-sm flex items-start justify-between group"
                          >
                            <p className="text-sm text-zinc-700 flex-1">{review.text}</p>
                            {canEdit && (
                              <Button
                                size="icon"
                                variant="ghost"
                                className="h-7 w-7 text-red-600 opacity-0 group-hover:opacity-100 transition-opacity ml-2"
                                onClick={() => handleDeleteTextReview(review.id)}
                              >
                                <Trash2 className="w-4 h-4" />
                              </Button>
                            )}
                          </div>
                        ))}
                      </div>
                    )}

                    {/* Add Text Review Input */}
                    {isReviewsSection && canEdit && (
                      <div className="mt-4 p-4 bg-zinc-50 rounded-sm">
                        <label className="block text-sm font-medium text-zinc-700 mb-2">
                          Add Text Review
                        </label>
                        <div className="flex flex-col gap-2">
                          <textarea
                            value={newReviewText}
                            onChange={(e) => setNewReviewText(e.target.value)}
                            placeholder="Enter customer review text..."
                            rows={3}
                            className="w-full px-3 py-2 border border-zinc-200 rounded-sm focus:outline-none focus:ring-2 focus:ring-zinc-200 text-sm resize-none"
                          />
                          <div className="flex justify-end">
                            <Button
                              onClick={handleAddTextReview}
                              disabled={addingReview || !newReviewText.trim()}
                              className="bg-zinc-900 hover:bg-zinc-800 text-white"
                            >
                              {addingReview ? (
                                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                              ) : (
                                <Plus className="w-4 h-4 mr-2" />
                              )}
                              Add Review
                            </Button>
                          </div>
                        </div>
                      </div>
                    )}
                  </div>
                )}
              </div>
            </div>
          );
        })}
      </div>

      {/* Reference Reels Section */}
      <div className="bg-white border border-zinc-200 rounded-sm overflow-hidden">
        <div className="px-6 py-4 bg-zinc-50 border-b border-zinc-200 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <Instagram className="w-5 h-5 text-pink-500" />
            <div>
              <h3 className="font-medium text-zinc-900">Reference Reels</h3>
              <p className="text-xs text-zinc-500">Add Instagram reel references for the editor (max 3). These will be carried over to the Production Pack.</p>
            </div>
          </div>
          <span className="text-xs text-zinc-400 bg-zinc-100 px-2 py-1 rounded">
            {(pack?.reference_reels || []).length}/3
          </span>
        </div>
        <div className="p-6 space-y-4">
          {/* Add reel input */}
          {canEdit && (pack?.reference_reels || []).length < 3 && (
            <div className="flex gap-2">
              <input
                type="url"
                value={reelUrl}
                onChange={(e) => setReelUrl(e.target.value)}
                placeholder="Paste Instagram reel URL..."
                data-testid="reference-reel-input"
                className="flex-1 px-3 py-2 border border-zinc-200 rounded-md focus:outline-none focus:ring-2 focus:ring-zinc-200 text-sm"
                onKeyDown={(e) => { if (e.key === 'Enter') handleAddReferenceReel(); }}
              />
              <Button onClick={handleAddReferenceReel} disabled={addingReel}
                className="bg-zinc-900 hover:bg-zinc-800 text-white" data-testid="add-reference-reel-btn">
                {addingReel ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Plus className="w-4 h-4 mr-2" />}
                Add
              </Button>
            </div>
          )}

          {/* Reels list */}
          {(pack?.reference_reels || []).length > 0 ? (
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              {(pack?.reference_reels || []).map((reel) => {
                let embedUrl = null;
                try {
                  const urlObj = new URL(reel.url);
                  embedUrl = `https://www.instagram.com${urlObj.pathname}embed/`;
                } catch {}
                return (
                  <div key={reel.id} className="bg-white border border-zinc-200 rounded-lg overflow-hidden"
                    data-testid={`reference-reel-${reel.id}`}>
                    <div className="aspect-[9/16] max-h-[350px] bg-zinc-100 relative">
                      {embedUrl ? (
                        <iframe src={embedUrl} className="w-full h-full border-0"
                          allowFullScreen scrolling="no" title="Instagram Reel" />
                      ) : (
                        <div className="w-full h-full flex items-center justify-center">
                          <a href={reel.url} target="_blank" rel="noopener noreferrer"
                            className="text-pink-500 hover:underline flex items-center gap-2">
                            <Instagram className="w-5 h-5" /> Open in Instagram
                          </a>
                        </div>
                      )}
                    </div>
                    <div className="p-3 border-t border-zinc-100 flex items-center justify-between">
                      <a href={reel.url} target="_blank" rel="noopener noreferrer"
                        className="text-sm text-pink-500 hover:underline truncate flex items-center gap-1">
                        <ExternalLink className="w-3 h-3" /> Open
                      </a>
                      {canEdit && (
                        <Button variant="ghost" size="sm" className="text-red-500 hover:text-red-600 hover:bg-red-50 h-7 w-7 p-0"
                          onClick={() => handleDeleteReferenceReel(reel.id)}>
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          ) : (
            <div className="text-center py-8 text-zinc-400 text-sm">
              No reference reels added yet
            </div>
          )}
        </div>
      </div>

      {/* Script Section */}
      <div className="bg-white border border-zinc-200 rounded-sm overflow-hidden">
        <div className="px-6 py-4 bg-zinc-50 border-b border-zinc-200 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <FileText className="w-5 h-5 text-zinc-600" />
            <div>
              <h3 className="font-medium text-zinc-900">Script</h3>
              <p className="text-xs text-zinc-500">Write the script for this Custom IP video reel</p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            {canEdit && (
              <Button
                size="sm"
                variant="outline"
                onClick={openGenerateDialog}
                className="text-amber-700 border-amber-200 bg-amber-50 hover:bg-amber-100"
                data-testid="generate-script-btn"
              >
                <Sparkles className="w-4 h-4 mr-1.5" />
                Generate with AI
              </Button>
            )}
            {canEdit && (
              <Button
                size="sm"
                onClick={handleSaveScript}
                disabled={savingScript}
                className="bg-zinc-900 hover:bg-zinc-800 text-white"
              >
                {savingScript ? (
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                ) : (
                  <Check className="w-4 h-4 mr-2" />
                )}
                Save Script
              </Button>
            )}
          </div>
        </div>
        <div className="p-6">
          <Textarea
            value={script}
            onChange={(e) => setScript(e.target.value)}
            placeholder="Write your script here... Describe the story, shots, and flow of the video reel."
            rows={10}
            disabled={!canEdit}
            className="w-full resize-none focus:ring-2 focus:ring-zinc-200"
          />
        </div>
      </div>

      {/* Generate Script Dialog */}
      <Dialog open={showGenDialog} onOpenChange={setShowGenDialog}>
        <DialogContent className="sm:max-w-2xl bg-white text-zinc-900 max-h-[90vh] overflow-y-auto" data-testid="generate-script-dialog">
          <DialogHeader>
            <DialogTitle className="text-lg font-semibold text-zinc-900 flex items-center gap-2">
              <Sparkles className="w-5 h-5 text-amber-600" />
              Generate Script in Influencer Style
            </DialogTitle>
            <DialogDescription className="text-zinc-500">
              Choose an analyzed influencer from Research — the script will blend this ring's story with their content style
            </DialogDescription>
          </DialogHeader>

          {loadingInfluencers ? (
            <div className="flex justify-center py-8"><Loader2 className="w-5 h-5 animate-spin text-zinc-400" /></div>
          ) : influencers.length === 0 ? (
            <div className="text-center py-8">
              <User className="w-8 h-8 text-zinc-300 mx-auto mb-2" />
              <p className="text-sm text-zinc-500">No analyzed influencers found</p>
              <p className="text-xs text-zinc-400 mt-1">Go to Research → add an influencer → upload videos → analyze their style first</p>
            </div>
          ) : (
            <>
              {/* Influencer picker */}
              <div className="space-y-2 my-2" data-testid="influencer-picker">
                {influencers.map(inf => (
                  <button
                    key={inf.id}
                    onClick={() => setSelectedInfluencer(inf.id)}
                    className={`w-full flex items-center gap-3 p-3 rounded-lg border transition-all text-left ${
                      selectedInfluencer === inf.id
                        ? 'border-amber-400 bg-amber-50 ring-1 ring-amber-200'
                        : 'border-zinc-200 bg-white hover:bg-zinc-50'
                    }`}
                    data-testid={`pick-influencer-${inf.id}`}
                  >
                    <div className="w-9 h-9 bg-zinc-100 rounded-full flex items-center justify-center shrink-0">
                      <User size={16} className="text-zinc-400" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-medium text-zinc-900">{inf.name}</p>
                      {inf.handle && <p className="text-xs text-zinc-400">@{inf.handle}</p>}
                    </div>
                    <div className="text-xs text-zinc-400">{inf.videos?.length || 0} videos analyzed</div>
                    {selectedInfluencer === inf.id && <CheckCircle size={18} className="text-amber-600 shrink-0" />}
                  </button>
                ))}
              </div>

              {/* Generate button */}
              <Button
                onClick={handleGenerateScript}
                disabled={generatingScript || !selectedInfluencer}
                className="w-full bg-amber-600 text-white hover:bg-amber-700 py-2.5"
                data-testid="confirm-generate-btn"
              >
                {generatingScript ? (
                  <><Loader2 size={14} className="mr-1.5 animate-spin" /> Generating script...</>
                ) : (
                  <><Sparkles size={14} className="mr-1.5" /> Generate Script</>
                )}
              </Button>

              {generatingScript && (
                <p className="text-xs text-zinc-400 text-center">Analyzing ring details and blending with influencer style... 30-60 seconds</p>
              )}

              {/* Generated result */}
              {generatedScript && (
                <div className="mt-4 border border-amber-200 rounded-lg overflow-hidden" data-testid="generated-script-result">
                  <div className="bg-amber-50 px-4 py-2.5 border-b border-amber-200 flex items-center justify-between">
                    <span className="text-xs font-semibold text-amber-800 uppercase tracking-wider">
                      Script — {generatedScript.influencer_name}'s Style
                    </span>
                    <div className="flex gap-1">
                      <Button variant="ghost" size="sm" className="h-7 text-xs text-amber-700" onClick={() => copyToClipboard(generatedScript.generated_script)}>
                        <Copy size={12} className="mr-1" /> Copy
                      </Button>
                    </div>
                  </div>
                  <div className="p-4 max-h-72 overflow-y-auto">
                    <div className="text-sm text-zinc-800 leading-relaxed whitespace-pre-wrap">{generatedScript.generated_script}</div>
                  </div>
                  <div className="px-4 py-3 bg-amber-50 border-t border-amber-200">
                    <Button onClick={applyGeneratedScript} className="w-full bg-zinc-900 text-white hover:bg-zinc-800" data-testid="apply-script-btn">
                      <Check size={14} className="mr-1.5" /> Apply to Script Field
                    </Button>
                  </div>
                </div>
              )}
            </>
          )}
        </DialogContent>
      </Dialog>

      {/* Action Buttons */}
      <div className="flex items-center justify-end gap-4 pt-4 border-t border-zinc-200">
        {canSubmit && (
          <Button
            onClick={handleSubmitForApproval}
            disabled={submitting}
            className="bg-amber-500 hover:bg-amber-600 text-white"
            data-testid="submit-for-approval-btn"
          >
            {submitting ? (
              <Loader2 className="w-4 h-4 mr-2 animate-spin" />
            ) : (
              <Send className="w-4 h-4 mr-2" />
            )}
            Submit for Approval
          </Button>
        )}
        {canApprove && (
          <Button
            onClick={handleApprove}
            disabled={approving}
            className="bg-green-600 hover:bg-green-700 text-white"
            data-testid="approve-btn"
          >
            {approving ? (
              <Loader2 className="w-4 h-4 mr-2 animate-spin" />
            ) : (
              <CheckCircle className="w-4 h-4 mr-2" />
            )}
            Approve & Create Production Pack
          </Button>
        )}
      </div>

      {/* Delete Media Confirmation */}
      <Dialog open={!!deleteMediaConfirm} onOpenChange={() => setDeleteMediaConfirm(null)}>
        <DialogContent className="sm:max-w-md bg-white">
          <DialogHeader>
            <DialogTitle>Delete File</DialogTitle>
            <DialogDescription>
              Are you sure you want to delete "{deleteMediaConfirm?.name}"? This action cannot be undone.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDeleteMediaConfirm(null)}>
              Cancel
            </Button>
            <Button
              onClick={handleDeleteMedia}
              className="bg-red-600 hover:bg-red-700 text-white"
            >
              Delete
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Media Preview */}
      <Dialog open={!!previewMedia} onOpenChange={() => setPreviewMedia(null)}>
        <DialogContent className="sm:max-w-4xl bg-black/95 border-none p-0">
          <Button
            size="icon"
            variant="ghost"
            className="absolute top-4 right-4 z-10 text-white hover:bg-white/20"
            onClick={() => setPreviewMedia(null)}
          >
            <X className="w-6 h-6" />
          </Button>
          {previewMedia && (
            <div className="flex items-center justify-center min-h-[400px]">
              {previewMedia.is_video ? (
                <video
                  src={previewMedia.file_path}
                  controls
                  autoPlay
                  className="max-w-full max-h-[80vh]"
                />
              ) : (
                <img
                  src={previewMedia.file_path}
                  alt={previewMedia.original_name}
                  className="max-w-full max-h-[80vh] object-contain"
                />
              )}
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default CustomIPDetail;
